import { Button, FormControl, makeStyles, TextField } from '@material-ui/core';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { Autocomplete } from '@material-ui/lab';

import { yupResolver } from '@hookform/resolvers/yup';
import { CharacterizationFilters, Client, Stream } from 'types';
import { colors } from 'theme';
import { useClients, useStreams } from 'hooks';
import DatePicker from 'components/CustomInputs/DatePicker';
import NextIcon from 'components/Icons/NextIcon';
import { atEndOfDay, atStartOfDay } from 'utils/date';
import { transformObjectId } from 'services/apiPlatformTransformer';
import { DEFAULT_ITEMS_PER_SELECT, TableFilterContext } from 'tableFilterContext';
import { dateFilterBarSchema } from 'components/Generic/DateFilterBar.yup';

const useStyles = makeStyles({
  fieldLine: {
    marginTop: 8,
    margin: '1vw',
    display: 'block',

    '& span': {
      fontSize: 18,
      margin: 5,
    },
  },
  datePicker: {
    '& label': {
      color: '#949FAC',
    },
    '& > div': {
      marginLeft: '25px',
      width: 150,
    },
    '&:last-of-type > div': {
      marginRight: '25px',
    },
  },
  submitButton: {
    marginTop: 24,
  },
  searchButton: {
    fontSize: 14,
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
  },
  searchButtonIcon: {
    marginLeft: '10px',
    width: 17,
    height: 17,
  },
  textField: {
    '& fieldset': {
      borderColor: colors.neutralGray,
    },
  },
  autocomplete: {
    width: 250,
    display: 'inline-block',
  },
  autocompleteControls: {
    top: '2px',
  },
  switch: {
    textAlign: 'right',
    color: colors.inputGrey,
  },
});

type CharacterizationFiltersEncoded = {
  startDate: number | null;
  endDate: number | null;
  client: string | null;
  stream: string | null;
};

type CharacterizationFilterBarProps = {
  onChange: (period: CharacterizationFilters) => void;
  minStartDate: Date;
  maxEndDate: Date;
  defaultStartDate?: Date;
  defaultEndDate?: Date;
};

export const CharacterizationFilterBar: React.FC<CharacterizationFilterBarProps> = ({
  onChange,
  minStartDate,
  maxEndDate,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { t: pt } = useTranslation('translation', { keyPrefix: 'characterizations.filter' });
  const [isReady, setReady] = useState<boolean>(false);
  const { dispatch, search } = useContext(TableFilterContext);
  const [defaultStreamValue, setDefaultStreamValue] = useState<Stream | null>(null);
  const [defaultClientValue, setDefaultClientValue] = useState<Client | null>(null);

  const [minEndDate, setMinEndDate] = useState<Date | null>(minStartDate);
  const [maxStartDate, setMaxStartDate] = useState<Date | null>(maxEndDate);

  const [streams] = useStreams(DEFAULT_ITEMS_PER_SELECT);
  const [clients] = useClients(DEFAULT_ITEMS_PER_SELECT);

  const schema = useMemo(
    () => dateFilterBarSchema(t, atStartOfDay(minStartDate), atEndOfDay(maxEndDate)),
    [t, minStartDate, maxEndDate],
  );

  const { control, trigger, handleSubmit, reset } = useForm<CharacterizationFilters>({
    mode: 'onBlur',
    defaultValues: {
      startDate: null,
      endDate: null,
    },
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    if (streams === null || clients === null) return;
    if (!search) {
      setReady(true);
      return;
    }
    const query: CharacterizationFiltersEncoded = JSON.parse(atob(search));
    if (query.client !== null) {
      setDefaultClientValue(clients.find(client => transformObjectId(client) === query.client) ?? null);
    }
    if (query.stream !== null) {
      setDefaultStreamValue(streams.find(stream => transformObjectId(stream) === query.stream) ?? null);
    }
    setReady(true);
  }, [search, setReady, streams, clients, setDefaultClientValue, setDefaultStreamValue]);

  useEffect(() => {
    if (!search) return;
    const query: CharacterizationFiltersEncoded = JSON.parse(atob(search));
    const filter = {
      startDate: query.startDate ? new Date(query.startDate) : null,
      endDate: query.endDate ? new Date(query.endDate) : null,
      client: query.client,
      stream: query.stream,
    };
    reset(filter);
    onChange(filter);
  }, [search, reset, onChange]);

  const saveSearchParams = useCallback(
    (e: CharacterizationFilters) => {
      dispatch({
        type: 'set_search',
        payload: btoa(
          JSON.stringify({
            startDate: e.startDate ? e.startDate.getTime() : null,
            endDate: e.endDate ? e.endDate.getTime() : null,
            client: e.client ?? null,
            stream: e.stream ?? null,
          }),
        ),
      });
    },
    [dispatch],
  );

  const submitPeriod = useCallback(
    (e: CharacterizationFilters) => {
      onChange(e);
      saveSearchParams(e);
    },
    [onChange, saveSearchParams],
  );

  return (
    <form onSubmit={handleSubmit(submitPeriod)}>
      <div className={classes.fieldLine}>
        {isReady && (
          <>
            <span>
              <Controller
                name="client"
                control={control}
                render={({ field: { ref, onChange, ...fieldProps } }) => (
                  <Autocomplete
                    options={clients ?? []}
                    size="small"
                    getOptionLabel={client => client.shortName}
                    defaultValue={defaultClientValue}
                    classes={{ root: classes.autocomplete, endAdornment: classes.autocompleteControls }}
                    noOptionsText={t('noResults')}
                    onChange={(_e, value: Client | null) => {
                      onChange({ target: { value: transformObjectId(value) } });
                    }}
                    renderInput={params => (
                      <TextField
                        {...params}
                        className={classes.textField}
                        {...fieldProps}
                        placeholder={pt('client')}
                        variant="outlined"
                      />
                    )}
                  />
                )}
              />
            </span>
            <span>
              <Controller
                name="stream"
                control={control}
                render={({ field: { ref, onChange, ...fieldProps } }) => (
                  <Autocomplete
                    size="small"
                    options={streams ?? []}
                    getOptionLabel={stream => stream.name}
                    defaultValue={defaultStreamValue}
                    classes={{ root: classes.autocomplete, endAdornment: classes.autocompleteControls }}
                    noOptionsText={t('noResults')}
                    onChange={(_e, value: Stream | null) => {
                      onChange({ target: { value: transformObjectId(value) } });
                    }}
                    renderInput={params => (
                      <TextField
                        {...params}
                        className={classes.textField}
                        {...fieldProps}
                        placeholder={pt('stream')}
                        variant="outlined"
                      />
                    )}
                  />
                )}
              />
            </span>
          </>
        )}
        <span className={classes.datePicker}>
          <Controller
            name="startDate"
            control={control}
            render={({ field: { ref, onChange, ...fieldProps }, fieldState }) => (
              <FormControl variant="outlined">
                <DatePicker
                  id="startDate"
                  data-qa-id="start-date-filter"
                  label={t('startDate')}
                  error={fieldState.invalid}
                  helperText={fieldState.error?.message}
                  inputRef={ref}
                  InputLabelProps={{ required: false }}
                  inputVariant="standard"
                  maxDate={maxStartDate}
                  minDate={minStartDate}
                  onAccept={() => {
                    trigger('startDate');
                  }}
                  onChange={e => {
                    onChange(e);
                    if (!e || (!!e && e < minStartDate)) {
                      setMinEndDate(minStartDate);
                      return;
                    }
                    setMinEndDate(e);
                  }}
                  {...fieldProps}
                />
              </FormControl>
            )}
          />
        </span>
        <span className={classes.datePicker}>
          <Controller
            name="endDate"
            control={control}
            render={({ field: { ref, onChange, ...fieldProps }, fieldState }) => (
              <FormControl variant="outlined">
                <DatePicker
                  id="endDate"
                  data-qa-id="end-date-filter"
                  label={t('endDate')}
                  error={fieldState.invalid}
                  helperText={fieldState.error?.message}
                  maxDate={maxEndDate}
                  minDate={minEndDate}
                  inputRef={ref}
                  inputVariant="standard"
                  InputLabelProps={{ required: false }}
                  onAccept={() => {
                    trigger('endDate');
                  }}
                  onChange={e => {
                    onChange(e);
                    if (!e || (!!e && e > maxEndDate)) {
                      setMaxStartDate(maxEndDate);
                      return;
                    }
                    setMaxStartDate(e);
                  }}
                  {...fieldProps}
                />
              </FormControl>
            )}
          />
        </span>
        <Button style={{ marginTop: 12 }} size="small" type="submit" variant="text">
          <div className={classes.searchButton}>{t('search')}</div>
          <NextIcon className={classes.searchButtonIcon} />
        </Button>
      </div>
    </form>
  );
};
